import { filterVisibleSections } from '@framework/engine/validator/status/utils/filterVisibleSections.ts';
import { isRuntimeSectionDisabled } from '@framework/engine/validator/status/utils/isRuntimeSectionDisabled.ts';
import { focusNextElement } from '@framework/ui/core/accessibility/focusNextElement.tsx';
import {
  type BrandedPath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import {
  type ItemType,
  type MenuItemType,
  type SubMenuType,
} from 'antd/es/menu/interface';
import { useMemo } from 'react';
import { useModuleFormSectionsMonitorContext } from '../../module/form/sections-monitor/ModuleFormSectionsMonitorContext.tsx';
import { ModuleValidationStateIconBadge } from '../../module/form/title/ModuleValidationStateIconBadge.tsx';
import {
  type ISubmissionMenuItem,
  SubmissionMenuItemType,
} from './ISubmissionMenuItem.tsx';
import { SubmissionMenuCountBadge } from './SubmissionMenuCountBadge.tsx';
import { SubmissionMenuDirtyBadge } from './SubmissionMenuDirtyBadge.tsx';
import { SubmissionMenuItem } from './SubmissionMenuItem.tsx';

/**
 * A partire da un array di oggetti di tipo IModuleMenuItem
 * li converte nelle Props per il componente Menu di Antd
 */
export function useSubmissionMenuPropsItems(params: {
  menu: ISubmissionMenuItem[];
  currentSubmissionModuleId: string | null | undefined;
  excludeSections?: boolean;
}): SubMenuType[] {
  const { menu, currentSubmissionModuleId, excludeSections } = params;
  const history = useHistory();

  const { runtimeSections } = useModuleFormSectionsMonitorContext();

  return useMemo(() => {
    return menu.map(mod => {
      const children =
        mod.children
          // Mostriamo la sezione solo se è visibile. Il calcolo viene fatto dal
          // FormHandler (vedi `ModuleFormSectionsMonitorContext`)
          ?.filter(i => {
            // L'anchor allegati non è una runtime section: lo mostriamo quando
            // siamo sulla pagina del modulo corrente, senza passare da
            // `filterVisibleSections`. Non lo condizioniamo al numero di
            // sezioni: il riepilogo allegati (`ModuleAttachmentsSummary`) è reso
            // per qualsiasi modulo con allegati, anche senza sezioni form, e
            // l'anchor deve seguire la stessa condizione.
            // NB: gli item `Attachments` vengono creati solo in BO (vedi
            // `addModuleAttachmentAnchors` in backoffice-web), quindi in FO
            // questo ramo non viene mai raggiunto.
            if (i.type === SubmissionMenuItemType.Attachments) {
              return (
                i.context?.submissionModuleId === currentSubmissionModuleId
              );
            }
            return filterVisibleSections({
              sectionId: i.context?.sectionId,
              submissionModuleId: i.context?.submissionModuleId,
              runtimeSections,
              currentSubmissionModuleId,
            });
          })
          .map(item => {
            const disabled =
              item.type === SubmissionMenuItemType.Attachments
                ? false
                : isRuntimeSectionDisabled(
                    runtimeSections,
                    item.context?.sectionId,
                  );

            return createSubmenuItem({
              item,
              disabled,
              onClick: e => {
                e.domEvent.stopPropagation();
                e.domEvent.preventDefault();
                if (item.to) {
                  history.push(item.to as BrandedPath);

                  // In caso di Sezioni (e dell'anchor Allegati), facciamo focus
                  // sull'elemento successivo all'anchor che identifica il target.
                  // Questi anchor sono tutti del tipo `#id`.
                  if (
                    item.type === SubmissionMenuItemType.Section ||
                    item.type === SubmissionMenuItemType.Attachments
                  ) {
                    const anchorElement = document.getElementById(
                      (item.to as string).substring(1),
                    );

                    focusNextElement(anchorElement);
                  }
                }
              },
            });
          }) ?? undefined;

      return {
        key: mod.key,
        icon: mod.icon,
        onClick: e => {
          // Se l'evento è stato già gestito da un MenuItem _interno_,
          // non effettuiamo il redirect: vuol dire che l'utente vuole
          // visitare una sezione interna al modulo.
          if (mod.to && !e.domEvent.isDefaultPrevented()) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            history.push(mod.to as BrandedPath);
          }
        },
        // onTitleClick: info => {
        //   console.log('onTitleClick > mod.to', mod.to);
        //   if (mod.to) {
        //     window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        //     console.log('onTitleClick > mod.to', mod.to);
        //     console.log('onTitleClick > mod.to info', info);
        //     history.push(mod.to as BrandedPath);
        //   }
        // },
        label: (
          <SubmissionMenuItem
            item={mod}
            badge={
              <ModuleValidationStateIconBadge
                type={mod.context?.state}
                validationState={mod.context?.validationState}
                hideIfNoOpen
              />
            }
          />
        ),
        children:
          !excludeSections && children && children.length > 0
            ? children
            : undefined,
      } as SubMenuType;
    });
  }, [
    menu,
    history,
    currentSubmissionModuleId,
    runtimeSections,
    excludeSections,
  ]);
}

function createSubmenuItem(input: {
  item: ISubmissionMenuItem;
  disabled: boolean;
  onClick: MenuItemType['onClick'];
}): ItemType<MenuItemType> {
  const { item, disabled, onClick } = input;
  return {
    key: item.key,
    disabled,
    onClick,
    // onTitleClick: info => onClick,
    icon: item.type === SubmissionMenuItemType.Section ? undefined : item.icon,

    label: (
      <SubmissionMenuItem
        item={item}
        disabled={disabled}
        badge={
          item.type === SubmissionMenuItemType.Attachments ? (
            <SubmissionMenuCountBadge count={item.count} />
          ) : (
            <SubmissionMenuDirtyBadge
              sectionId={item.key}
              submissionModuleId={item.context?.submissionModuleId}
            />
          )
        }
      />
    ),
  };
}
